// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { MoreVert } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { GenericAction, GenericRow } from '../pages/collateral/collaterals/types';

type TypographyColorFactory<R extends GenericRow<unknown>> = (
  action: GenericAction<R>,
) => keyof Theme['palette'];

type ActionItemProps<R extends GenericRow<unknown>> = {
  row: R;
  action: GenericAction<R>;
  typographyColorFactory?: TypographyColorFactory<R>;
};

const ActionItem = <R extends GenericRow<unknown>>({
  action: a,
  row: r,
  typographyColorFactory,
}: ActionItemProps<R>) => {
  const [isLoading, setIsLoading] = useState(false);

  const { showBoundary } = useErrorBoundary<Error>();

  const handleClick = () => {
    const result = a.action(r);

    if (result instanceof Promise) {
      setIsLoading(true);

      result.catch((error: Error) => showBoundary(error)).finally(() => setIsLoading(false));
    }
  };

  const menuItemSx: SxProps = {
    position: 'relative',
  };

  const progressWrapperSx: SxProps = {
    display: 'flex',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };

  const typographySx: SxProps = {
    opacity: isLoading ? 0 : 1,
  };

  const typographyColor = typographyColorFactory ? typographyColorFactory(a) : undefined;

  return (
    <MenuItem
      key={a.name}
      onClick={() => handleClick()}
      disabled={a.disabled || isLoading}
      sx={menuItemSx}
    >
      {isLoading && (
        <Box sx={progressWrapperSx}>
          <CircularProgress size={16} />
        </Box>
      )}
      <Typography sx={typographySx} color={typographyColor} variant="body2">
        {a.name}
      </Typography>
    </MenuItem>
  );
};

type MoreActionsMenuProps<R extends GenericRow<unknown>> = {
  actions: GenericAction<R>[];
  row: R;
  setSelectedRow: (r: R) => void;
  typographyColorFactory?: TypographyColorFactory<R>;
};

export const MoreActionsMenu = <R extends GenericRow<unknown>>({
  actions,
  row,
  setSelectedRow,
  typographyColorFactory,
}: MoreActionsMenuProps<R>) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box>
      <Box sx={{ ml: 'auto' }}>
        <IconButton aria-label="More options" onClick={handleClick}>
          <MoreVert />
        </IconButton>
      </Box>
      <Menu
        sx={{ pt: 0, pb: 0 }}
        id="simple-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {actions?.map((a) => {
          return (
            <ActionItem
              key={a.name}
              action={a}
              row={row}
              typographyColorFactory={typographyColorFactory}
            />
          );
        })}
      </Menu>
    </Box>
  );
};
